import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Delete, Edit, Save, Cancel, Category, Info } from '@mui/icons-material';
import { holidaysAPI } from '../services/api';
import Swal from 'sweetalert2';

const HolidayCategoryManagement = () => {
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    description: '',
    min: 2,
    max: 3,
    rules: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await holidaysAPI.getCategories(new Date().getFullYear());
      
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load holiday categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      category: '',
      name: '',
      description: '',
      min: 2,
      max: 3,
      rules: ''
    });
    setIsEditMode(false);
    setSelectedCategory(null);
    setShowDialog(true);
  };

  const handleEdit = (catKey) => {
    if (!categories || !categories[catKey]) return;
    
    const cat = categories[catKey];
    setFormData({
      category: catKey,
      name: cat.name || '',
      description: cat.description || '',
      min: cat.min || 2,
      max: cat.max || 3,
      rules: cat.rules ? JSON.stringify(cat.rules) : ''
    });
    setIsEditMode(true);
    setSelectedCategory(catKey);
    setShowDialog(true);
  };

  const handleDelete = async (catKey) => {
    const result = await Swal.fire({
      title: 'Delete Category',
      text: `Are you sure you want to delete Category ${catKey}? This will not delete the holidays in this category.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
      background: 'rgb(10, 25, 41)',
      color: '#D4AF37',
      cancelButtonColor: '#666',
    });

    if (result.isConfirmed) {
      try {
        // In a real implementation, you'd call an API to delete the category
        // For now, we'll just show a message
        await Swal.fire({
          icon: 'info',
          title: 'Not Implemented',
          text: 'Category deletion would need a backend endpoint. Holidays in this category will remain but without category assignment.',
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
          confirmButtonColor: '#D4AF37',
        });
      } catch (err) {
        console.error('Error deleting category:', err);
        setError('Failed to delete category');
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!formData.category || !formData.name) {
        setError('Category letter and name are required');
        setSaving(false);
        return;
      }

      // In a real implementation, you'd call an API to save the category
      // For now, we'll just show a success message and reload
      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Category ${formData.category} ${isEditMode ? 'updated' : 'created'} successfully. Note: This is a frontend demo - backend API needed for persistence.`,
        timer: 3000,
        showConfirmButton: false,
        background: 'rgb(10, 25, 41)',
        color: '#D4AF37',
      });

      setShowDialog(false);
      loadCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryColor = (cat) => {
    switch(cat) {
      case 'A': return '#2196F3';
      case 'B': return '#4CAF50';
      case 'C': return '#FF9800';
      default: return '#D4AF37';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <Category sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
            Holiday Categories Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNew}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Category
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#EF5350', border: '1px solid #EF5350' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#81C784', border: '1px solid #81C784' }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Categories Table */}
        <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgb(10, 25, 35)' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Min</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Max</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Rules</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories && ['A', 'B', 'C'].map(catKey => {
                    const cat = categories[catKey];
                    if (!cat) return null;

                    return (
                      <TableRow key={catKey} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                        <TableCell>
                          <Chip
                            label={`Cat ${catKey}`}
                            sx={{
                              bgcolor: `${getCategoryColor(catKey)}20)`,
                              color: getCategoryColor(catKey),
                              border: `1px solid ${getCategoryColor(catKey)}`,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                            {cat.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {cat.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={cat.min} size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#81C784' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={cat.max} size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#FFB74D' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                            {cat.rules?.note || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(catKey)}
                              sx={{ color: '#D4AF37', mr: 1, '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(catKey)}
                              sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239,83,80,0.1)' } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!categories || ['A', 'B', 'C'].every(k => !categories[k])) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#B4C4A8' }}>No categories found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Category Dialog */}
        <Dialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            {isEditMode ? 'Edit Holiday Category' : 'Add New Holiday Category'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: '#B4C4A8' }}>Category Letter</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                }}
                disabled={isEditMode}
              >
                <MenuItem value="A">Category A - National Holidays</MenuItem>
                <MenuItem value="B">Category B - Regional Festivals</MenuItem>
                <MenuItem value="C">Category C - Religious Holidays</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mt: 2, mb: 2 }}
              required
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField
              fullWidth
              label="Minimum Holidays"
              type="number"
              value={formData.min}
              onChange={(e) => setFormData({ ...formData, min: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField
              fullWidth
              label="Maximum Holidays"
              type="number"
              value={formData.max}
              onChange={(e) => setFormData({ ...formData, max: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField
              fullWidth
              label="Rules (JSON, Optional)"
              multiline
              rows={2}
              value={formData.rules}
              onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Enter rules in JSON format"
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button
              onClick={() => setShowDialog(false)}
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              <Cancel sx={{ mr: 1 }} />
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              <Save sx={{ mr: 1 }} />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default HolidayCategoryManagement;
